import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Table, Button, Form, InputGroup, Card, Pagination, Modal, Spinner, Badge } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEdit, FaTrash } from 'react-icons/fa';
import { adminApi } from '../../services/api';
import Swal from 'sweetalert2';

const BankList = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);

  useEffect(() => {
    document.title = 'Bank Management';
    fetchBanks();
  }, []);

  const fetchBanks = async (currentPage = 1, limit = 10, search = '') => {
    setLoading(true);
    try {
      const params = {
        skip: (currentPage - 1) * limit,
        limit: limit,
        search: search
      };
      const response = await adminApi.getBanks(params);
      setBanks(response.data.banks);
      setTotalItems(response.data.total);
      setPage(currentPage);
    } catch (error) {
      console.error('Error fetching banks:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch banks'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    fetchBanks(pageNumber, pageSize, searchText);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBanks(1, pageSize, searchText);
  };

  const confirmDelete = (bank) => {
    setBankToDelete(bank);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!bankToDelete) return;
    
    try {
      await adminApi.deleteBank(bankToDelete.id);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Bank deleted successfully'
      });
      setShowDeleteModal(false);
      fetchBanks(page, pageSize, searchText);
    } catch (error) {
      console.error('Error deleting bank:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete bank'
      });
    }
  };

  // Bootstrap table doesn't need columns definition like Ant Design
  // Instead we'll render the table structure directly in the JSX
  // Calculate pagination details
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);
  
  // Generate pagination items
  const getPaginationItems = () => {
    let items = [];
    
    // Previous button
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => handlePageChange(page - 1)} 
        disabled={page === 1} 
      />
    );
    
    // First page
    items.push(
      <Pagination.Item 
        key={1} 
        active={page === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );
    
    // Ellipsis if needed
    if (page > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }
    
    // Pages around current page
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      if (i === 1 || i === totalPages) continue; // Skip first and last page as they're added separately
      items.push(
        <Pagination.Item 
          key={i} 
          active={page === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Ellipsis if needed
    if (page < totalPages - 2) {
      items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }
    
    // Last page if it exists
    if (totalPages > 1) {
      items.push(
        <Pagination.Item 
          key={totalPages} 
          active={page === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    // Next button
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => handlePageChange(page + 1)} 
        disabled={page === totalPages} 
      />
    );
    
    return items;
  };

  return (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Header as="h5" className="bg-white">
          <Row className="align-items-center">
            <Col>Bank Management</Col>
            <Col xs="auto">
              <Link to="/admin/banks/add">
                <Button variant="primary">
                  <FaPlus className="me-2" /> Add Bank
                </Button>
              </Link>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-4">
            <Row className="align-items-end">
              <Col md={6} lg={4}>
                <InputGroup>
                  <Form.Control
                    placeholder="Search banks"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Button variant="outline-secondary" type="submit">
                    <FaSearch />
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading banks...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped hover className="align-middle">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Bank Name</th>
                      <th>Bank Slug</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banks.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          No banks found
                        </td>
                      </tr>
                    ) : (
                      banks.map(bank => (
                        <tr key={bank.id}>
                          <td>{bank.id}</td>
                          <td>{bank.bank}</td>
                          <td>{bank.bank_slug}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Link to={`/admin/banks/edit/${bank.id}`} className="btn btn-sm btn-primary">
                                <FaEdit className="me-1" /> Edit
                              </Link>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => confirmDelete(bank)}
                              >
                                <FaTrash className="me-1" /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalItems > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Showing {startItem} to {endItem} of {totalItems} banks
                  </div>
                  <Pagination>{getPaginationItems()}</Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete bank "{bankToDelete?.bank}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BankList;